/**
 * Hook de récupération des mesures journalières du lot ouvert d'un bâtiment,
 * et mapping vers les indicateurs affichés par RegulatorWidget.
 *
 * Règle de fond : tout indicateur sans source réelle dans la donnée vaut `undefined`,
 * jamais une valeur de repli. Le widget affiche alors « — », ce qui vaut mieux qu'un
 * chiffre inventé sur un écran d'élevage.
 *
 * Contrat JSON (depuis le 2026-07-29) : les champs numériques sont des `*float64` côté Go,
 * donc `0` = mesuré à zéro et `null` = non mesuré. Ne jamais tester la véracité d'un nombre.
 *
 * Agrégation, différente selon la nature de la grandeur :
 *  - AMBIANCE → dernier jour mesuré (surveillance : une moyenne noie un pic) ;
 *  - CONSO / ÉNERGIE / poids / GMQ → moyenne journalière sur la fenêtre (débits) ;
 *  - effectif, stock silo, panel → dernière valeur connue (états instantanés) ;
 *  - mortalité → cumul sur la fenêtre.
 */
import { useCallback, useEffect, useState } from "react";
import {
  DailyMeasureEntry,
  fetchRegulatorMeasures,
  RegulatorFetchError,
  RegulatorFetchFailure,
  RegulatorMeasuresResponse,
} from "../../../../api/regulator";
import { isLegacyFirmwareType } from "../widgetCatalog";
import { legacyToDailyMeasures } from "./legacyMeasures";

/** Plage journalière min/max. Une seule des deux bornes peut être mesurée. */
export type MeasureRange = { min?: number; max?: number };

/**
 * Grandeurs affichables en ligne d'indicateur. Sur-ensemble de `RegulatorChartType` : certaines
 * s'affichent mais ne se tracent pas (`silos` est une liste, `waterFeedRatio` un rapport).
 */
export type RegulatorMetric =
  | RegulatorChartType
  | "homogeneity"
  | "panel"
  | "waterFeedRatio"
  | "silos";

/** Métriques traçables, groupées par famille (pour le sélecteur de courbe). */
export type RegulatorChartType =
  | "temperatureInside"
  | "temperatureOutside"
  | "humidityInside"
  | "humidityOutside"
  | "co2"
  | "nh3"
  | "airflow"
  | "light"
  | "weight"
  | "gmq"
  | "dead"
  | "animalsTotal"
  | "water"
  | "food"
  | "siloStock"
  | "gaz"
  | "electricity";

/**
 * Point de courbe. `min`/`max` alimentent la bande d'amplitude quand la métrique est une
 * plage ; `value` est la série principale (le max pour une plage, la valeur pour un scalaire).
 */
export type RegulatorChartPoint = {
  time: string;
  value: number;
  min?: number;
  max?: number;
};

/** Un silo tel que le document le décrit (conso du jour + stock restant). */
export type SiloIndicator = {
  name?: string;
  consumption?: number;
  stock?: number;
};

/** Détail des causes de mortalité, cumulées sur la fenêtre. */
export type DeadBreakdown = {
  natural?: number;
  eliminated?: number;
  takenout?: number;
  cardiac?: number;
  total?: number;
};

/** Indicateurs prêts à afficher, tous optionnels (voir la règle ci-dessus). */
export type RegulatorMeasureIndicators = {
  // ── Ambiance ────────────────────────────────────────────────────────────
  temperatureInside?: MeasureRange;
  temperatureOutside?: MeasureRange;
  humidityInside?: MeasureRange;
  humidityOutside?: MeasureRange;
  co2?: MeasureRange;
  nh3?: MeasureRange;
  airflow?: number;
  light?: number;
  // ── Cheptel ─────────────────────────────────────────────────────────────
  animalsTotal?: number;
  weightAverage?: number;
  gmq?: number;
  homogeneity?: number;
  panel?: number;
  /** Mortalité cumulée sur la FENÊTRE (pas depuis le début du lot), par cause. */
  dead?: DeadBreakdown;
  /** Genres présents dans la donnée (mixed | male | female). */
  kinds?: string[];
  // ── Consommation ────────────────────────────────────────────────────────
  waterTotalLiters?: number;
  feedTotalKg?: number;
  waterPerHeadMl?: number;
  feedPerHeadG?: number;
  /** Ratio eau/aliment (L/kg) — calculé seulement si les deux sources existent. */
  waterFeedRatio?: number;
  silos?: SiloIndicator[];
  // ── Énergie ─────────────────────────────────────────────────────────────
  gaz?: number;
  electricity?: number;
};

export type UseRegulatorMeasuresResult = {
  indicators: RegulatorMeasureIndicators;
  /** Points de la courbe (axe = date du jour de mesure), vides si la métrique n'a pas de source. */
  chartHistory: RegulatorChartPoint[];
  /**
   * Nombre de jours de mesure reçus sur la fenêtre. À ne PAS confondre avec
   * `chartHistory.length` : l'API peut renvoyer 8 jours dont aucun ne porte la métrique
   * choisie. « Pas de donnée du tout » et « cette courbe-là est vide » sont deux
   * messages différents pour l'utilisateur.
   */
  daysCount: number;
  /** Jour d'élevage du dernier jour reçu (`day` du document). */
  latestDay?: number;
  /**
   * Date du dernier jour reçu (YYYY-MM-DD). Les valeurs d'ambiance venant de ce jour-là, il
   * faut pouvoir le dire : un régulateur muet depuis 3 jours afficherait sinon des chiffres
   * qu'on croirait actuels.
   */
  latestDate?: string;
  /**
   * Bâtiment portant le régulateur, résolu par DATA. `undefined` quand le produit n'est installé
   * nulle part — état NORMAL (un régulateur n'est obligatoirement lié qu'à une entreprise), à
   * distinguer d'une absence de mesures.
   */
  buildingId?: number;
  /** Type du produit (serenity, avitouch, avibox…) : détermine les grandeurs qu'il sait remonter. */
  deviceType?: string;
  /** Droits bigdata sur le bâtiment de ce régulateur ; toujours un tableau. */
  granted: string[];
  /** Faux si aucun GroupAccess : pas de licence du tout sur ce bâtiment. */
  hasGroupAccess: boolean;
  /** Vrai quand le bâtiment n'a aucun lot ouvert : à distinguer d'une absence de mesures. */
  noOpenBatch: boolean;
  batchName?: string;
  /** Fenêtre réellement interrogée, telle que DATA l'a bornée. */
  window?: { from: string; to: string };
  /** Forme des mesures : "legacy" n'est affichée que pour le firmware Avibox (avibox, labelbox). */
  source?: "serenity" | "legacy";
  loading: boolean;
  /** Cause de l'échec du dernier chargement, `""` s'il a réussi. */
  error: RegulatorFetchError | "";
  /** Relance le chargement (bouton « Réessayer »). */
  reload: () => void;
};

/**
 * Normalise une valeur du backend : `null` (non mesuré) devient `undefined`, un **0 est
 * conservé** — c'est une vraie mesure depuis le passage en `*float64` côté Go. Ne jamais
 * remplacer ce helper par un test de véracité (`if (value)`), qui écraserait les 0 légitimes.
 */
function measured(value?: number | null): number | undefined {
  return value === null || value === undefined ? undefined : value;
}

/** Somme d'une liste de compteurs (eau/aliment en exposent plusieurs par bâtiment). */
function sumList(values?: number[] | null): number | undefined {
  if (!values || values.length === 0) return undefined;
  return values.reduce((total, value) => total + value, 0);
}

/** Une plage n'existe que si au moins une borne a été mesurée (0 compris). */
function toRange(raw?: {
  min?: number | null;
  max?: number | null;
}): MeasureRange | undefined {
  if (!raw) return undefined;
  const min = measured(raw.min);
  const max = measured(raw.max);
  if (min === undefined && max === undefined) return undefined;
  return { min, max };
}

/** Valeur représentative d'une plage : le max, sinon le min. */
export function rangeValue(range?: MeasureRange): number | undefined {
  if (!range) return undefined;
  return range.max ?? range.min;
}

/**
 * Agrège les `animals` d'un jour, indexés par genre (mixed | male | female) : effectifs et
 * morts s'additionnent, les poids se moyennent sur les genres réellement pesés.
 */
function readAnimals(entry: DailyMeasureEntry["data"]) {
  const kinds = Object.entries(entry?.animals ?? {});
  if (kinds.length === 0) return {};

  // Chaque agrégat reste `undefined` tant qu'AUCUN genre n'a porté la valeur ; dès qu'un
  // genre l'a mesurée — même à 0 — le total existe. C'est tout l'intérêt du passage en
  // `*float64` côté Go : « 0 mort » est une information, « pas de remontée » une autre.
  let total: number | undefined;
  let dead: DeadBreakdown | undefined;
  let weightSum: number | undefined;
  let weightCount = 0;
  let gmqSum: number | undefined;
  let gmqCount = 0;
  let homogeneitySum: number | undefined;
  let homogeneityCount = 0;
  let panel: number | undefined;

  kinds.forEach(([, kind]) => {
    const kindTotal = measured(kind.total);
    if (kindTotal !== undefined) total = (total ?? 0) + kindTotal;

    if (kind.dead) {
      const add = (
        current: number | undefined,
        value?: number | null
      ): number | undefined => {
        const next = measured(value);
        return next === undefined ? current : (current ?? 0) + next;
      };
      dead = {
        natural: add(dead?.natural, kind.dead.natural),
        eliminated: add(dead?.eliminated, kind.dead.eliminated),
        takenout: add(dead?.takenout, kind.dead.takenout),
        cardiac: add(dead?.cardiac, kind.dead.cardiac),
      };
    }

    const average = measured(kind.weight?.average);
    if (average !== undefined) {
      weightSum = (weightSum ?? 0) + average;
      weightCount += 1;
    }
    const gmq = measured(kind.weight?.gmq);
    if (gmq !== undefined) {
      gmqSum = (gmqSum ?? 0) + gmq;
      gmqCount += 1;
    }
    const homogeneity = measured(kind.weight?.homogeneity);
    if (homogeneity !== undefined) {
      homogeneitySum = (homogeneitySum ?? 0) + homogeneity;
      homogeneityCount += 1;
    }
    const kindPanel = measured(kind.weight?.panel);
    if (kindPanel !== undefined) panel = (panel ?? 0) + kindPanel;
  });

  if (dead) {
    const causes = [dead.natural, dead.eliminated, dead.takenout, dead.cardiac];
    // Le total n'existe que si au moins une cause a été mesurée (sinon le bloc `dead`
    // était présent mais entièrement vide, ce qui ne dit rien).
    dead.total = causes.some((cause) => cause !== undefined)
      ? causes.reduce((sum, cause) => (sum ?? 0) + (cause ?? 0), 0)
      : undefined;
  }

  return {
    total,
    dead,
    kinds: kinds.map(([name]) => name),
    weightAverage:
      weightCount > 0 && weightSum !== undefined
        ? weightSum / weightCount
        : undefined,
    gmq: gmqCount > 0 && gmqSum !== undefined ? gmqSum / gmqCount : undefined,
    homogeneity:
      homogeneityCount > 0 && homogeneitySum !== undefined
        ? homogeneitySum / homogeneityCount
        : undefined,
    panel,
  };
}

/** Lecture d'une métrique pour un jour : plage (min/max) ou valeur simple. */
function metricAt(
  chartType: RegulatorChartType,
  entry: DailyMeasureEntry
): MeasureRange | undefined {
  const data = entry.data;
  if (!data) return undefined;
  // Un scalaire devient une plage réduite à son max : 0 reste une valeur traçable.
  const asValue = (value?: number | null): MeasureRange | undefined => {
    const kept = measured(value);
    return kept === undefined ? undefined : { max: kept };
  };

  switch (chartType) {
    case "temperatureInside":
      return toRange(data.ambience?.temperature?.inside);
    case "temperatureOutside":
      return toRange(data.ambience?.temperature?.outside);
    case "humidityInside":
      return toRange(data.ambience?.humidity?.inside);
    case "humidityOutside":
      return toRange(data.ambience?.humidity?.outside);
    case "co2":
      return toRange(data.ambience?.co2);
    case "nh3":
      return toRange(data.ambience?.nh3);
    case "airflow":
      return asValue(data.ambience?.airflow);
    case "light":
      return asValue(data.ambience?.light);
    case "weight":
      return asValue(readAnimals(data).weightAverage);
    case "gmq":
      return asValue(readAnimals(data).gmq);
    case "dead":
      return asValue(readAnimals(data).dead?.total);
    case "animalsTotal":
      return asValue(readAnimals(data).total);
    case "water":
      return asValue(sumList(data.consumption?.water));
    case "food":
      return asValue(sumList(data.consumption?.food));
    case "siloStock":
      return asValue(
        sumList(
          data.consumption?.silo
            ?.map((silo) => measured(silo.stock))
            .filter((stock): stock is number => stock !== undefined)
        )
      );
    case "gaz":
      return asValue(data.energy?.gaz);
    case "electricity":
      return asValue(data.energy?.electricty);
    default:
      return undefined;
  }
}

/**
 * Dernier jour renseigné pour une lecture. Réservé aux ÉTATS INSTANTANÉS (effectif, stock de
 * silo) : moyenner un stock qui se vide, ou un effectif qui décroît, donnerait un chiffre qui
 * n'a existé aucun jour.
 */
function latestDefined<T>(
  entries: DailyMeasureEntry[],
  read: (entry: DailyMeasureEntry) => T | undefined
): T | undefined {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const value = read(entries[index]);
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

/**
 * Moyenne sur la fenêtre pour les MESURES PHYSIQUES (températures, hygrométrie, gaz, conso…).
 *
 * C'est le comportement attendu par l'utilisateur : sur une série `0, 1, 2, 1, 0, 0`, on veut
 * 0,67 — la moyenne des 6 jours — et non `0` parce que le dernier jour vaut 0. Les zéros sont
 * comptés comme de vraies valeurs (cohérent avec le contrat `*float64` : un 0 est mesuré, un
 * `null` ne l'est pas et n'entre pas dans le calcul).
 */
function averageOf(
  entries: DailyMeasureEntry[],
  read: (entry: DailyMeasureEntry) => number | undefined
): number | undefined {
  let sum = 0;
  let count = 0;
  entries.forEach((entry) => {
    const value = read(entry);
    if (value !== undefined && value !== null) {
      sum += value;
      count += 1;
    }
  });
  return count > 0 ? sum / count : undefined;
}

export function buildIndicators(
  measures: DailyMeasureEntry[]
): RegulatorMeasureIndicators {
  if (measures.length === 0) return {};

  // Mortalité cumulée sur la FENÊTRE interrogée, pas depuis le début du lot : measuresdata
  // ne contient que les jours demandés. Sur un lot long (pondeuses, 300+ jours) l'écart avec
  // la mortalité totale est énorme — d'où le libellé explicite côté widget.
  const dead = measures.reduce<DeadBreakdown | undefined>((cumul, entry) => {
    const daily = readAnimals(entry.data).dead;
    if (!daily) return cumul;
    return {
      natural: (cumul?.natural ?? 0) + (daily.natural ?? 0),
      eliminated: (cumul?.eliminated ?? 0) + (daily.eliminated ?? 0),
      takenout: (cumul?.takenout ?? 0) + (daily.takenout ?? 0),
      cardiac: (cumul?.cardiac ?? 0) + (daily.cardiac ?? 0),
      total: (cumul?.total ?? 0) + (daily.total ?? 0),
    };
  }, undefined);

  // Consommations : moyenne journalière sur la fenêtre (une conso nulle un jour compte, elle
  // fait partie de la réalité de la période).
  const waterTotalLiters = averageOf(measures, (entry) =>
    sumList(entry.data?.consumption?.water)
  );
  const feedTotalKg = averageOf(measures, (entry) =>
    sumList(entry.data?.consumption?.food)
  );
  // Effectif : ÉTAT instantané, on prend le dernier connu (un « effectif moyen » n'existe pas).
  const animalsTotal = latestDefined(
    measures,
    (entry) => readAnimals(entry.data).total
  );

  return {
    // AMBIANCE = SURVEILLANCE, donc DERNIER JOUR mesuré (pas une moyenne de période).
    // Un éleveur ouvre son dashboard pour savoir « où en suis-je maintenant » : une moyenne
    // sur 8 jours noie un pic nocturne à 32 °C dans une moyenne à 24 °C, alors que c'est
    // précisément le pic qui tue des animaux. La tendance de la période est portée par la
    // courbe, pas par le chiffre. (Décision UX du 2026-07-30.)
    temperatureInside: latestDefined(measures, (entry) =>
      toRange(entry.data?.ambience?.temperature?.inside)
    ),
    temperatureOutside: latestDefined(measures, (entry) =>
      toRange(entry.data?.ambience?.temperature?.outside)
    ),
    humidityInside: latestDefined(measures, (entry) =>
      toRange(entry.data?.ambience?.humidity?.inside)
    ),
    humidityOutside: latestDefined(measures, (entry) =>
      toRange(entry.data?.ambience?.humidity?.outside)
    ),
    co2: latestDefined(measures, (entry) => toRange(entry.data?.ambience?.co2)),
    nh3: latestDefined(measures, (entry) => toRange(entry.data?.ambience?.nh3)),
    airflow: latestDefined(measures, (entry) =>
      measured(entry.data?.ambience?.airflow)
    ),
    light: latestDefined(measures, (entry) =>
      measured(entry.data?.ambience?.light)
    ),

    animalsTotal,
    // Poids/GMQ/homogénéité : mesures physiques, moyennées sur les jours réellement pesés.
    weightAverage: averageOf(
      measures,
      (entry) => readAnimals(entry.data).weightAverage
    ),
    gmq: averageOf(measures, (entry) => readAnimals(entry.data).gmq),
    homogeneity: averageOf(
      measures,
      (entry) => readAnimals(entry.data).homogeneity
    ),
    // Panel : dernier effectif pesé connu (un état, pas une grandeur à moyenner).
    panel: latestDefined(measures, (entry) => readAnimals(entry.data).panel),
    dead,
    // Genres du dernier jour qui en déclare (un jour sans bloc `animals` n'en dit rien).
    kinds: latestDefined(measures, (entry) => {
      const kinds = readAnimals(entry.data).kinds;
      return kinds && kinds.length > 0 ? kinds : undefined;
    }),

    waterTotalLiters,
    feedTotalKg,
    // Par tête : seulement si l'effectif est connu ET non nul (sinon division absurde).
    waterPerHeadMl:
      waterTotalLiters !== undefined && animalsTotal
        ? (waterTotalLiters * 1000) / animalsTotal
        : undefined,
    feedPerHeadG:
      feedTotalKg !== undefined && animalsTotal
        ? (feedTotalKg * 1000) / animalsTotal
        : undefined,
    waterFeedRatio:
      waterTotalLiters !== undefined && feedTotalKg
        ? waterTotalLiters / feedTotalKg
        : undefined,
    silos: latestDefined(measures, (entry) => {
      const silos = entry.data?.consumption?.silo;
      if (!silos || silos.length === 0) return undefined;
      // Normalise chaque borne : un silo à 0 kg de stock est une info (silo vide),
      // un `null` signifie que le régulateur ne remonte pas ce compteur.
      return silos.map((silo) => ({
        name: silo.name,
        consumption: measured(silo.consumption),
        stock: measured(silo.stock),
      }));
    }),

    // Énergie : consommations journalières, moyennées comme l'eau et l'aliment.
    gaz: averageOf(measures, (entry) => measured(entry.data?.energy?.gaz)),
    electricity: averageOf(measures, (entry) =>
      measured(entry.data?.energy?.electricty)
    ),
  };
}

/** Courbe de la métrique demandée : un point par jour qui a réellement une valeur. */
export function buildChartHistory(
  measures: DailyMeasureEntry[],
  chartType: RegulatorChartType
): RegulatorChartPoint[] {
  return measures
    .map((entry) => {
      const range = metricAt(chartType, entry);
      const value = rangeValue(range);
      if (value === undefined) return null;
      // Libellé d'axe : le jour calendaire (JJ/MM), la fenêtre courant sur plusieurs dates.
      const date = entry.data?.date?.slice(0, 10);
      const label = date
        ? `${date.slice(8, 10)}/${date.slice(5, 7)}`
        : `J${entry.data?.day ?? "?"}`;
      // min/max ne sont portés que si la métrique est une vraie plage (min distinct du max) :
      // inutile de dessiner une bande d'épaisseur nulle sur un scalaire.
      const hasBand = range?.min !== undefined && range.min !== range.max;
      return {
        time: label,
        value,
        ...(hasBand ? { min: range.min, max: range.max } : {}),
      };
    })
    .filter((point): point is RegulatorChartPoint => point !== null);
}

type Settled = {
  uuid: string;
  attempt: number;
  response?: RegulatorMeasuresResponse;
  error: RegulatorFetchError | "";
};

export function useRegulatorMeasures(
  deviceUuid?: string,
  chartType: RegulatorChartType = "temperatureInside"
): UseRegulatorMeasuresResult {
  const [attempt, setAttempt] = useState(0);
  // Résultat rattaché au régulateur ET à la tentative qui l'ont produit : en changeant de
  // régulateur ou en relançant, l'ancien résultat n'est plus affiché, et le chargement se voit
  // dès le premier rendu (avant, « aucun bâtiment » s'affichait le temps de l'appel).
  const [settled, setSettled] = useState<Settled>();
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // UN SEUL appel : DATA résout régulateur → bâtiment → lot → mesures, et joint les droits.
  useEffect(() => {
    if (!deviceUuid) return;
    let active = true;
    fetchRegulatorMeasures(deviceUuid)
      .then((response) => {
        if (active)
          setSettled({ uuid: deviceUuid, attempt, response, error: "" });
      })
      .catch((err: unknown) => {
        const error =
          err instanceof RegulatorFetchFailure ? err.reason : "unavailable";
        if (active) setSettled({ uuid: deviceUuid, attempt, error });
      });
    return () => {
      active = false;
    };
  }, [deviceUuid, attempt]);

  const current =
    settled && settled.uuid === deviceUuid && settled.attempt === attempt
      ? settled
      : undefined;
  const response = current?.response;
  // Legacy Avibox : converti au format journalier, le reste du widget n'en sait rien.
  const measures =
    response?.source === "legacy" && isLegacyFirmwareType(response.device_type)
      ? legacyToDailyMeasures(
          response.legacy_measures ?? [],
          response.entrance_date
        )
      : (response?.measures ?? []);
  const indicators = buildIndicators(measures);

  return {
    indicators,
    chartHistory: buildChartHistory(measures, chartType),
    daysCount: measures.length,
    latestDay: measures[measures.length - 1]?.data?.day,
    latestDate: measures[measures.length - 1]?.data?.date?.slice(0, 10),
    buildingId: response?.building_id,
    deviceType: response?.device_type,
    granted: response?.granted ?? [],
    hasGroupAccess: response?.has_group_access ?? false,
    // Un lot ouvert absent est une info métier, pas une erreur : le widget le dit autrement.
    // Seulement pertinent si le régulateur EST installé quelque part.
    noOpenBatch:
      response !== undefined &&
      response.building_id !== undefined &&
      response.batch_id === "",
    batchName: response?.batch_name,
    window: response ? { from: response.from, to: response.to } : undefined,
    source: response?.source,
    loading: Boolean(deviceUuid) && current === undefined,
    error: current?.error ?? "",
    reload,
  };
}
